import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';
import { buildPreviewOrders, type PreviewOrder } from './order-preview-fixtures';
import { loadPreviewDraft, savePreviewDraft } from './order-import-drafts';

export function useOrderImportDraft(draftKey: string) {
  const [initial] = useState(() => {
    const base = buildPreviewOrders();
    return { base, ...loadPreviewDraft(draftKey, base) };
  });
  const [orders, setOrderState] = useState<readonly PreviewOrder[]>(initial.orders);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>(initial.status === 'restored' ? 'saved' : initial.status === 'failed' ? 'failed' : 'idle');
  const [savedAt, setSavedAt] = useState(initial.savedAt);
  const ordersRef = useRef(initial.orders);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const saveNow = useCallback(() => {
    clearTimeout(timer.current);
    if (!dirty.current) return true;
    const saved = savePreviewDraft(draftKey, ordersRef.current, initial.base);
    setState(saved ? 'saved' : 'failed');
    if (saved) { dirty.current = false; setSavedAt(Date.now()); }
    return saved;
  }, [draftKey, initial.base]);

  const setOrders = useCallback((change: SetStateAction<readonly PreviewOrder[]>) => {
    const next = typeof change === 'function' ? change(ordersRef.current) : change;
    if (next === ordersRef.current) return;
    ordersRef.current = next;
    dirty.current = true;
    setOrderState(next);
    setState('saving');
  }, []);

  useEffect(() => {
    if (!dirty.current) return;
    timer.current = setTimeout(saveNow, 600);
    return () => clearTimeout(timer.current);
  }, [orders, saveNow]);

  useEffect(() => {
    const saveOnHide = () => { if (document.hidden) saveNow(); };
    window.addEventListener('pagehide', saveNow);
    document.addEventListener('visibilitychange', saveOnHide);
    return () => {
      window.removeEventListener('pagehide', saveNow);
      document.removeEventListener('visibilitychange', saveOnHide);
    };
  }, [saveNow]);

  function retry() {
    if (dirty.current) return saveNow();
    const loaded = loadPreviewDraft(draftKey, initial.base);
    if (loaded.status === 'failed') return false;
    ordersRef.current = loaded.orders;
    setOrderState(loaded.orders);
    setSavedAt(loaded.savedAt);
    setState(loaded.status === 'restored' ? 'saved' : 'idle');
    return true;
  }
  return { orders, setOrders, state, savedAt, saveNow, retry };
}
